import React, { useEffect, useState, useRef, Fragment, Component } from 'react';
import { Shield, Zap, Check, ArrowRight, AlertTriangle, Download, ChevronDown, ChevronUp, Clock, CreditCard, Star, X, MessageSquare, AlertCircle, Brain, DollarSign, Video, HardHat, CircuitBoard, Cpu, Settings, Target, TrendingUp, Lightbulb, Headphones, ThumbsUp, BookOpen, Lock, Gift, Smartphone, ShoppingCart } from 'lucide-react';
import { useMetaPixel } from '../hooks/useMetaPixel';
import { usePurchaseNotifications } from '../hooks/usePurchaseNotifications';
import { PurchaseNotification } from '../components/PurchaseNotification';
import { ProblemSolutionSection } from '../components/ProblemSolutionSection';
import { MaterialPreviewSection } from '../components/MaterialPreviewSection';
import { MaestroElectricModules } from '../components/MaestroElectricModules';
import { MaestroElectricBonuses } from '../components/MaestroElectricBonuses';
const heroSheets = [{
  src: 'https://cdn.magicpatterns.com/uploads/db3hL9hHM3MZdapYCmdckH/pagina_062_bomba_flotante_tanque.webp',
  alt: 'Lámina eléctrica: bomba y flotante de tanque',
  x: -168,
  y: 18,
  rotate: -16,
  scale: 0.88,
  depth: 1
}, {
  src: 'https://cdn.magicpatterns.com/uploads/cQYvzZUum8obWQ5mAk2hbM/pagina_007_luz_cuatro_puntos.webp',
  alt: 'Lámina eléctrica: luz comandada desde cuatro puntos',
  x: 168,
  y: 18,
  rotate: 16,
  scale: 0.88,
  depth: 1
}, {
  src: 'https://cdn.magicpatterns.com/uploads/mmJ3yB5Mh9Bx4XhMUcVWmk/pagina_011_plafon_led.webp',
  alt: 'Lámina eléctrica: plafón LED',
  x: -136,
  y: 8,
  rotate: -11,
  scale: 0.92,
  depth: 2
}, {
  src: 'https://cdn.magicpatterns.com/uploads/xkvCSNprYUJgreZ8FSFMXF/pagina_014_tiras_led_fuente.webp',
  alt: 'Lámina eléctrica: tiras LED y fuente',
  x: 136,
  y: 8,
  rotate: 11,
  scale: 0.92,
  depth: 2
}, {
  src: 'https://cdn.magicpatterns.com/uploads/jy21WpoBpsJRyTSTqJxB89/pagina_024_varios_tomacorrientes.webp',
  alt: 'Lámina eléctrica: tomacorrientes en paralelo',
  x: -108,
  y: -2,
  rotate: -7,
  scale: 0.96,
  depth: 3
}, {
  src: 'https://cdn.magicpatterns.com/uploads/qSwgrFdthJMuV58dR2doVa/pagina_035_fotocelula_reflectores.webp',
  alt: 'Lámina eléctrica: fotocélula y reflectores',
  x: 108,
  y: -2,
  rotate: 7,
  scale: 0.96,
  depth: 3
}, {
  src: 'https://cdn.magicpatterns.com/uploads/uJaw3sjX41sP41HrscjcAs/pagina_046_tablero_circuitos_separados.webp',
  alt: 'Lámina eléctrica: tablero con circuitos separados',
  x: -82,
  y: -12,
  rotate: -3.5,
  scale: 1,
  depth: 4
}, {
  src: 'https://cdn.magicpatterns.com/uploads/sfBjnKtSxWrsbh8nXBF2w2/pagina_051_aire_acondicionado.webp',
  alt: 'Lámina eléctrica: aire acondicionado',
  x: 82,
  y: -12,
  rotate: 3.5,
  scale: 1,
  depth: 4
}];

// FAQ Component
function FAQItem({
  question,
  answer
}: {
  question: string;
  answer: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="border border-gray-200 rounded-lg mb-3 overflow-hidden bg-white" data-id="element-143" data-mp="5qyzn44tojh6">
      <button onClick={() => setIsOpen(!isOpen)} className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-gray-50 transition-colors" data-id="element-144" data-mp="5p4cdj62wf9x">
        <span className="font-medium text-gray-900 pr-4" data-id="element-145" data-mp="uaitrds88cwk">{question}</span>
        {isOpen ? <ChevronUp className="w-5 h-5 text-blue-600 flex-shrink-0" data-id="element-146" data-mp="ffcnqqt4se38" /> : <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" data-id="element-147" data-mp="kdnv8uvyecia" />}
      </button>
      {isOpen && <div className="px-6 py-4 bg-gray-50 border-t border-gray-200" data-id="element-148" data-mp="6m9bumy4w9ap">
          <p className="text-gray-700 leading-relaxed" data-id="element-149" data-mp="6ky98qgfd7l6">{answer}</p>
        </div>}
    </div>;
}
// Image Carousel Component
function ImageCarousel({
  images
}: {
  images: Array<{
    image: string;
    caption: string;
  }>;
}) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  useEffect(() => {
    if (isPaused) return;
    const interval = setInterval(() => {
      setCurrentIndex(prev => prev === images.length - 1 ? 0 : prev + 1);
    }, 4000);
    return () => clearInterval(interval);
  }, [images.length, isPaused]);
  const handleTouchStart = (e: React.TouchEvent | React.MouseEvent) => {
    setIsPaused(true);
    setTimeout(() => setIsPaused(false), 5000);
    if ('touches' in e) {
      setTouchStart(e.targetTouches[0].clientX);
    } else {
      setTouchStart((e as React.MouseEvent).clientX);
    }
  };
  const handleTouchMove = (e: React.TouchEvent | React.MouseEvent) => {
    if ('touches' in e) {
      setTouchEnd(e.targetTouches[0].clientX);
    } else {
      setTouchEnd((e as React.MouseEvent).clientX);
    }
  };
  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    if (distance > 50) setCurrentIndex(prev => prev === images.length - 1 ? 0 : prev + 1);
    if (distance < -50) setCurrentIndex(prev => prev === 0 ? images.length - 1 : prev - 1);
    setTouchEnd(0);
    setTouchStart(0);
  };
  return <div className="relative max-w-2xl mx-auto touch-pan-y" onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd} onMouseDown={handleTouchStart} onMouseMove={handleTouchMove} onMouseUp={handleTouchEnd} onMouseLeave={handleTouchEnd} data-id="element-150" data-mp="ptl9zu1s44ft">
      <div className="relative bg-gray-50 border-2 border-gray-200 rounded-xl overflow-hidden shadow-lg select-none cursor-grab active:cursor-grabbing" data-id="element-151" data-mp="13kgphp6s9jsd">
        <div className="aspect-[3/4] relative" data-id="element-152" data-mp="6virzz7nf7ri">
          <img src={images[currentIndex].image} alt={images[currentIndex].caption} className="w-full h-full object-cover transition-opacity duration-500 pointer-events-none" data-id="element-153" data-mp="avszxhf04890" />
        </div>
        <div className="p-4 bg-white border-t border-gray-100" data-id="element-154" data-mp="fx8in2tpi2o8">
          <p className="text-sm font-medium text-gray-700 text-center" data-id="element-155" data-mp="kvju1hzngl8k">
            {images[currentIndex].caption}
          </p>
        </div>
      </div>
      <div className="flex justify-center gap-2 mt-6" data-id="element-156" data-mp="1w8vs1c8qa9mt">
        {images.map((_, index) => <button key={index} onClick={() => {
        setCurrentIndex(index);
        setIsPaused(true);
        setTimeout(() => setIsPaused(false), 5000);
      }} className={`h-2 rounded-full transition-all duration-300 ${index === currentIndex ? 'bg-blue-600 w-8' : 'bg-gray-300 w-2'}`} aria-label={`Ir a imagen ${index + 1}`} data-id="element-157" data-mp="1wayn4aoqmsye" data-mpi={index} />)}
      </div>
    </div>;
}
// Dynamic Stats Hook
function useDynamicStats() {
  const [viewers, setViewers] = useState(47);
  const [spots, setSpots] = useState(9);
  useEffect(() => {
    const viewerInterval = setInterval(() => {
      setViewers(prev => {
        const change = Math.floor(Math.random() * 5) - 2;
        return Math.max(40, Math.min(55, prev + change));
      });
    }, Math.random() * 5000 + 8000);
    const spotInterval = setInterval(() => {
      setSpots(prev => prev > 3 && Math.random() > 0.65 ? prev - 1 : prev);
    }, 40000);
    return () => {
      clearInterval(viewerInterval);
      clearInterval(spotInterval);
    };
  }, []);
  return {
    viewers,
    spots
  };
}
// Upsell Stats Hook
function useUpsellStats() {
  const [viewers, setViewers] = useState(62);
  const [spots, setSpots] = useState(12);
  const [professionals, setProfessionals] = useState(5851);
  useEffect(() => {
    const viewerInterval = setInterval(() => {
      setViewers(prev => {
        const change = Math.floor(Math.random() * 5) - 2;
        return Math.max(55, Math.min(75, prev + change));
      });
    }, 5000);
    const spotInterval = setInterval(() => {
      setSpots(prev => {
        if (prev > 2) {
          setProfessionals(p => p + 1);
          return prev - 1;
        }
        return prev;
      });
    }, 40000);
    return () => {
      clearInterval(viewerInterval);
      clearInterval(spotInterval);
    };
  }, []);
  return {
    viewers,
    spots,
    professionals
  };
}
// Upsell Section Component
function UpsellSection() {
  const {
    viewers,
    spots,
    professionals
  } = useUpsellStats();
  const today = new Date();
  const formattedDate = today.toLocaleDateString('es-AR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long'
  });
  const displayDate = formattedDate.charAt(0).toUpperCase() + formattedDate.slice(1);
  return;
}
// Review Form Component
function ReviewForm({
  onSubmitReview
}: {
  onSubmitReview: (review: {
    name: string;
    text: string;
    stars: number;
  }) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [name, setName] = useState('');
  const [comment, setComment] = useState('');
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState('');
  const handleSubmit = () => {
    if (!name.trim()) {
      setError('Por favor ingresá tu nombre');
      return;
    }
    if (rating === 0) {
      setError('Por favor seleccioná una calificación');
      return;
    }
    if (!comment.trim()) {
      setError('Por favor escribí un comentario');
      return;
    }
    setError('');
    setShowSuccess(true);
    onSubmitReview({
      name: name.trim(),
      text: comment.trim(),
      stars: rating
    });
    setName('');
    setComment('');
    setRating(0);
    setTimeout(() => {
      setShowSuccess(false);
      setIsOpen(false);
    }, 3000);
  };
  if (!isOpen) {
    return <div className="text-center mt-8" data-id="element-158" data-mp="3os3alxm7ubf1">
        <button onClick={() => setIsOpen(true)} className="text-gray-500 hover:text-blue-600 text-sm font-medium underline transition-colors" data-id="element-159" data-mp="3tq4u2jnk0cr1">
          ¿Compraste? Dejá tu reseña
        </button>
      </div>;
  }
  return <div className="bg-white border border-gray-200 rounded-xl p-6 mt-8 max-w-2xl mx-auto shadow-sm relative animate-fade-in-up" data-id="element-160" data-mp="7a0j6rspf4k2">
      {showSuccess && <div className="absolute top-4 right-4 bg-green-600 text-white px-4 py-2 rounded-lg shadow-lg text-sm font-medium z-10" data-id="element-161" data-mp="cbg2off8h30c">
          <p data-id="element-162" data-mp="l9s1v5u6194e">¡Gracias por tu reseña!</p>
        </div>}
      <div className="flex justify-between items-center mb-4" data-id="element-163" data-mp="lakyvf3bok9c">
        <h4 className="font-bold text-gray-900 flex items-center gap-2" data-id="element-164" data-mp="1g7zulgmoiu15">
          <MessageSquare className="w-5 h-5 text-blue-600" data-id="element-165" data-mp="1l6b5luopnaky" />
          Dejá tu reseña
        </h4>
        <button onClick={() => setIsOpen(false)} className="text-gray-400 hover:text-gray-600" data-id="element-166" data-mp="1q5eb1g8lritg">
          <X className="w-5 h-5" data-id="element-167" data-mp="1uw4geth13lo5" />
        </button>
      </div>
      <div className="space-y-4" data-id="element-168" data-mp="1uxnuqej4jmna">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-169" data-mp="1yxq0ozvw728d">
          <input type="text" placeholder="Tu nombre" value={name} onChange={e => setName(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all" data-id="element-170" data-mp="1yx6jxmhzajvx" />
          <div className="flex items-center gap-1 px-4 py-2 border border-gray-300 rounded-lg bg-white" data-id="element-171" data-mp="2dvibxfuep4gr">
            {[1, 2, 3, 4, 5].map(s => <Star key={s} className={`w-5 h-5 cursor-pointer transition-colors ${s <= (hoverRating || rating) ? 'fill-orange-400 text-orange-400' : 'text-gray-300'}`} onMouseEnter={() => setHoverRating(s)} onMouseLeave={() => setHoverRating(0)} onClick={() => setRating(s)} data-id="element-172" data-mp="ej638k8ka6yg" />)}
          </div>
        </div>
        <textarea placeholder="Contanos tu experiencia..." rows={3} value={comment} onChange={e => setComment(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all resize-none" data-id="element-173" data-mp="2ryxv5pdyya9w" />
        {error && <p className="text-red-500 text-sm" data-id="element-174" data-mp="3fpjqcg2vk02h">{error}</p>}
        <button onClick={handleSubmit} className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-6 rounded-lg transition-colors text-sm" data-id="element-175" data-mp="3bori9q5qo17s">
          Enviar reseña
        </button>
      </div>
    </div>;
}
type ExampleComment = {
  name: string;
  msg: string;
};
function TestimonialItem({
  t
}: {
  t: ExampleComment;
}) {
  return <div className="flex items-start gap-3" data-id="element-176" data-mp="1vb1deh6sp8x3">
      <div className="w-9 h-9 bg-green-600 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0" data-id="element-177" data-mp="1qb55w7yw2zcs">
        {t.name.charAt(0)}
      </div>
      <div className="flex-grow" data-id="element-178" data-mp="1rq7ux2xhr2qq">
        <p className="mb-1 font-bold text-sm text-gray-800" data-id="element-179" data-mp="1wr5ar8v13kp2">{t.name}</p>
        <div className="chat-bubble" data-id="element-180" data-mp="1wpay0htwuyw2">
          <p className="text-sm text-gray-800 leading-relaxed" data-id="element-181" data-mp="1is5qedejtryp">{t.msg}</p>
        </div>
      </div>
    </div>;
}
export function LandingPage() {
  const {
    trackEvent
  } = useMetaPixel();
  const {
    notification,
    isVisible
  } = usePurchaseNotifications();
  const {
    viewers,
    spots
  } = useDynamicStats();
  const [argentinaNow, setArgentinaNow] = useState(() => new Date());
  const formattedDate = argentinaNow.toLocaleDateString('es-AR', {
    timeZone: 'America/Argentina/Buenos_Aires',
    weekday: 'long',
    day: 'numeric',
    month: 'long'
  });
  const displayDate = formattedDate.charAt(0).toUpperCase() + formattedDate.slice(1);
  const [headerVisible, setHeaderVisible] = useState(false);
  const [problemRealVisible, setProblemRealVisible] = useState(false);
  const [decisionVisible, setDecisionVisible] = useState(false);
  const [benefitsVisible, setBenefitsVisible] = useState(false);
  const [postHeroVisible, setPostHeroVisible] = useState(false);
  const [visibleTestimonials, setVisibleTestimonials] = useState(5);
  const [finalComboVisible, setFinalComboVisible] = useState(false);
  const [includedContentVisible, setIncludedContentVisible] = useState(false);
  const [heroFanVisible, setHeroFanVisible] = useState(false);
  const [heroFanProgress, setHeroFanProgress] = useState(0);
  const [heroFanExpanded, setHeroFanExpanded] = useState(false);
  const [heroFanPulse, setHeroFanPulse] = useState(false);
  const [heroFanPulseAnimating, setHeroFanPulseAnimating] = useState(false);
  const finalComboRef = useRef<HTMLElement>(null);
  const includedContentRef = useRef<HTMLDivElement>(null);
  const heroSectionRef = useRef<HTMLElement>(null);
  const heroFanRef = useRef<HTMLDivElement>(null);
  const heroFanProgressRef = useRef(0);
  const heroFanTargetRef = useRef(0);
  const heroFanVisibleRef = useRef(false);
  const heroInactivityResetRef = useRef<() => void>(() => {});
  const heroTouchTimerRef = useRef<number | null>(null);
  const suppressHeroBookClickRef = useRef(false);
  const allChatReviews: ExampleComment[] = [{
    name: 'Javier M.',
    msg: 'Me vino bárbaro para repasar unas conexiones que hacía rato no tocaba. Está todo bastante a mano.'
  }, {
    name: 'Gustavo R.',
    msg: 'Lo que más uso son los esquemas. Antes tenía que andar buscando videos para acordarme de alguna conexión.'
  }, {
    name: 'Pablo S.',
    msg: 'Justo estaba con una instalación de bomba y flotante. Me sirvió tener el esquema para revisarlo tranquilo.'
  }, {
    name: 'Andrés C.',
    msg: 'Está bueno que tenga separado iluminación, tomas y tableros. Encontrás rápido lo que buscás.'
  }, {
    name: 'Matías B.',
    msg: 'Lo abrí desde el celu mientras preparaba los materiales para un laburo. Muy práctico tener todo junto.'
  }];
  // Scroll reveal for sections
  useEffect(() => {
    const revealObserver = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-reveal');
          revealObserver.unobserve(entry.target);
        }
      });
    }, {
      threshold: 0.1
    });
    document.querySelectorAll('[data-reveal]').forEach(el => {
      revealObserver.observe(el);
    });
    return () => revealObserver.disconnect();
  }, []);
  // Sticky offer appears only after the hero leaves the viewport.
  useEffect(() => {
    const hero = heroSectionRef.current;
    if (!hero) return;
    const observer = new IntersectionObserver(([entry]) => {
      setHeaderVisible(!entry.isIntersecting && window.scrollY > 0);
    }, {
      threshold: 0
    });
    observer.observe(hero);
    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          if (entry.target.id === 'problem-real-section') setProblemRealVisible(true);
          if (entry.target.id === 'decision-section') setDecisionVisible(true);
          if (entry.target.id === 'benefits-section') setBenefitsVisible(true);
          if (entry.target.id === 'post-hero-section') setPostHeroVisible(true);
        }
      });
    }, {
      threshold: 0.15
    });
    const sections = ['problem-real-section', 'decision-section', 'benefits-section', 'post-hero-section'];
    sections.forEach(id => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);
  const scrollToPurchase = () => {
    document.getElementById('compra')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const scrollToSolution = () => {
    document.getElementById('solution')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const buildCheckoutUrl = () => {
    const checkoutUrl = new URL('https://100-instalaciones-resueltas.impultienda.ar/checkout');
    const campaignParams = new URLSearchParams(window.location.search);
    campaignParams.forEach((value, key) => checkoutUrl.searchParams.set(key, value));
    return checkoutUrl.toString();
  };
  const handlePurchaseClick = () => {
    trackEvent('InitiateCheckout');
    window.location.href = buildCheckoutUrl();
  };
  useEffect(() => {
    if (typeof window === 'undefined' || window.__metaViewContentTracked) return;
    trackEvent('ViewContent', {
      content_name: '+100 Instalaciones Eléctricas Resueltas',
      content_type: 'product'
    });
    window.__metaViewContentTracked = true;
  }, [trackEvent]);
  useEffect(() => {
    const section = finalComboRef.current;
    if (!section) return;
    const observer = new IntersectionObserver(entries => {
      if (entries[0]?.isIntersecting) {
        setFinalComboVisible(true);
        observer.disconnect();
      }
    }, {
      threshold: 0.2
    });
    observer.observe(section);
    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    const summary = includedContentRef.current;
    if (!summary) return;
    const observer = new IntersectionObserver(entries => {
      if (entries[0]?.isIntersecting) {
        setIncludedContentVisible(true);
        observer.disconnect();
      }
    }, {
      threshold: 0.35
    });
    observer.observe(summary);
    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    const stage = heroFanRef.current;
    if (!stage) return;
    const heroBook = new Image();
    heroBook.src = 'https://cdn.magicpatterns.com/uploads/r7JhcpdYiLyVgbmAQHch52/Diseno_sin_titulo_(5).webp';
    heroSheets.forEach(sheet => {
      const image = new Image();
      image.src = sheet.src;
    });
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reducedMotion) {
      heroFanProgressRef.current = 1;
      heroFanTargetRef.current = 1;
      setHeroFanProgress(1);
      setHeroFanVisible(true);
      return;
    }
    let hasRevealed = false;
    let hasPulsed = false;
    let hasAutoPulseCancelled = false;
    let revealTimer = 0;
    let inactivityTimer = 0;
    let pulseReturnTimer = 0;
    let pulseEndTimer = 0;
    let frameId = 0;
    let lastScrollY = window.scrollY;
    const renderProgress = () => {
      frameId = 0;
      const current = heroFanProgressRef.current;
      const target = heroFanTargetRef.current;
      const next = Math.abs(target - current) < 0.002 ? target : current + (target - current) * 0.045;
      heroFanProgressRef.current = next;
      setHeroFanProgress(next);
      if (next !== target) frameId = window.requestAnimationFrame(renderProgress);
    };
    const animateToTarget = () => {
      if (!frameId) frameId = window.requestAnimationFrame(renderProgress);
    };
    const setTargetProgress = (value: number) => {
      heroFanTargetRef.current = Math.max(0, Math.min(1, value));
      animateToTarget();
    };
    const cancelInactivityPulse = () => {
      window.clearTimeout(inactivityTimer);
      window.clearTimeout(pulseReturnTimer);
      window.clearTimeout(pulseEndTimer);
      setHeroFanPulse(false);
      setHeroFanPulseAnimating(false);
    };
    const scheduleInactivityPulse = () => {
      if (!heroFanVisibleRef.current || hasPulsed || hasAutoPulseCancelled || !hasRevealed) return;
      window.clearTimeout(inactivityTimer);
      inactivityTimer = window.setTimeout(() => {
        if (!heroFanVisibleRef.current || hasPulsed) return;
        hasPulsed = true;
        setHeroFanPulseAnimating(true);
        setHeroFanPulse(true);
        pulseReturnTimer = window.setTimeout(() => setHeroFanPulse(false), 600);
        pulseEndTimer = window.setTimeout(() => setHeroFanPulseAnimating(false), 1200);
      }, 7000);
    };
    heroInactivityResetRef.current = () => {
      hasAutoPulseCancelled = true;
      cancelInactivityPulse();
    };
    const visibilityObserver = new IntersectionObserver(entries => {
      const isIntersecting = entries[0]?.isIntersecting ?? false;
      heroFanVisibleRef.current = isIntersecting;
      if (isIntersecting && !hasRevealed) {
        hasRevealed = true;
        setHeroFanVisible(true);
        revealTimer = window.setTimeout(() => {
          setTargetProgress(1);
          scheduleInactivityPulse();
        }, 1500);
      }
      if (isIntersecting && hasRevealed && !hasPulsed && !hasAutoPulseCancelled) scheduleInactivityPulse();
      if (!isIntersecting) cancelInactivityPulse();
    }, {
      threshold: 0.16
    });
    visibilityObserver.observe(stage);
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const delta = currentScrollY - lastScrollY;
      lastScrollY = currentScrollY;
      if (!heroFanVisibleRef.current || !hasRevealed || Math.abs(delta) < 4) return;
      hasAutoPulseCancelled = true;
      cancelInactivityPulse();
      setTargetProgress(heroFanTargetRef.current + delta * 0.004);
    };
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => {
      visibilityObserver.disconnect();
      window.removeEventListener('scroll', handleScroll);
      window.clearTimeout(revealTimer);
      cancelInactivityPulse();
      heroInactivityResetRef.current = () => {};
      if (heroTouchTimerRef.current) window.clearTimeout(heroTouchTimerRef.current);
      if (frameId) window.cancelAnimationFrame(frameId);
    };
  }, []);
  useEffect(() => {
    const interval = window.setInterval(() => setArgentinaNow(new Date()), 60_000);
    return () => window.clearInterval(interval);
  }, []);
  const contentImages = [{
    image: 'https://cdn.magicpatterns.com/uploads/6N9aigZzaNsoRe37pWdoFH/WhatsApp_Image_2026-01-17_at_13.42.51.jpg',
    caption: 'Diagramas explicados paso a paso'
  }, {
    image: 'https://cdn.magicpatterns.com/uploads/aRdF2bWiMHEmctuHXsJVR9/WhatsApp_Image_2026-01-17_at_13.42.50_(3).jpg',
    caption: 'Entendé la lógica del circuito, no solo la conexión'
  }, {
    image: 'https://cdn.magicpatterns.com/uploads/fuLknpKftbKZSC5DGAQ2tZ/WhatsApp_Image_2026-01-17_at_13.42.50_(2).jpg',
    caption: 'Casos prácticos aplicados al trabajo real'
  }, {
    image: 'https://cdn.magicpatterns.com/uploads/aGWc1fPM9DTj6kfR5oKjFr/WhatsApp_Image_2026-01-17_at_13.42.50_(1).jpg',
    caption: 'Material pensado para uso profesional'
  }, {
    image: 'https://cdn.magicpatterns.com/uploads/dZNF3VrgumsrSXiMRXLWTh/WhatsApp_Image_2026-01-17_at_13.42.49.jpg',
    caption: 'Explicaciones técnicas claras y directas'
  }];
  const showMoreTestimonials = () => {
    setVisibleTestimonials(prev => Math.min(prev + 5, allChatReviews.length));
  };
  const handleSubmitReview = (review: {
    name: string;
    text: string;
    stars: number;
  }) => {
    // Handle review submission
  };
  const problemItems = [{
    icon: AlertTriangle,
    title: 'EL MIEDO A LO "DIFÍCIL"',
    desc: 'Sentir que la automatización es solo para ingenieros o genios de la matemática, cuando en realidad es pura lógica de campo.'
  }, {
    icon: Lock,
    title: 'EL MURO LABORAL',
    desc: 'Ver que todos los puestos bien pagados en la industria exigen PLC y sentir que te quedás afuera por no tener la base técnica.'
  }, {
    icon: Zap,
    title: 'EL TECHO DEL ELECTRICISTA',
    desc: 'Estar cansado del esfuerzo físico extremo y los trabajos mal pagados de siempre, buscando pasar a la parte inteligente del rubro.'
  }, {
    icon: Brain,
    title: 'EL LABERINTO DE YOUTUBE',
    desc: 'Perder meses con tutoriales sueltos que no tienen un orden lógico y te terminan dejando más confundido que al principio.'
  }];
  const ecosystemItems = [{
    icon: Video,
    title: '+100 CLASES',
    desc: 'Desde cero hasta programación avanzada.'
  }, {
    icon: Cpu,
    title: 'SOFTWARE Y SIMULADORES',
    desc: 'TIA Portal, Studio 5000 y RSLogix 500.'
  }, {
    icon: Download,
    title: 'MANUALES PDF',
    desc: 'Diagramas, guías y documentación técnica.'
  }, {
    icon: HardHat,
    title: 'VIRTUALBOX',
    desc: 'Entorno virtual preconfigurado para practicar.'
  }, {
    icon: CircuitBoard,
    title: 'CURSOS COMPLEMENTARIOS',
    desc: 'HMI, SCADA y redes industriales: Profinet / Modbus.'
  }];
  return <div className="min-h-screen bg-gray-50 font-sans text-gray-800" data-id="element-182" data-mp="1lkmp3umgi3uz">
      {/* Sticky offer — shown after the hero */}
      <header className={`fixed left-0 right-0 top-0 z-40 bg-[#0B1F3B] py-3 text-white shadow-md transition-[transform,opacity] duration-200 ${headerVisible ? 'animate-header-slide' : 'pointer-events-none -translate-y-full opacity-0'}`} data-id="element-183" data-mp="o00g8do7e0i2">
        <div className="container mx-auto flex items-center justify-between px-4" data-id="element-184" data-mp="12mtm6sbu1txl">
          <div className="flex min-w-0 flex-1 items-center gap-2" data-id="element-185" data-mp="12owi1szo2ha8">
            <div className="h-9 w-9 flex-shrink-0 overflow-hidden rounded-full border-2 border-[#F5C844]/60" data-id="element-186" data-mp="5768o4swtanv">
              <img src="https://cdn.magicpatterns.com/uploads/8PWeJYoMntMjFBhKbpgq4k/Emblema_de_puno_electrico_2.0.png" alt="Emblema Maestro Eléctrico 2.0" className="h-full w-full object-cover" data-id="element-187" data-mp="a4xymt9zumpf" />
            </div>
            <span className="truncate text-xs font-black tracking-wide text-white sm:text-sm" data-id="element-188" data-mp="3u82nwjico5hd">MAESTRO ELÉCTRICO</span>
          </div>
          <button onClick={scrollToPurchase} className="ml-3 inline-flex flex-shrink-0 items-center whitespace-nowrap rounded-lg bg-[#F5C844] px-3 py-2 text-xs font-black text-[#0B1F3B] transition-[background-color,transform] duration-200 hover:bg-[#ffe078] sm:px-4 sm:text-sm" data-id="element-189" data-mp="6wb7qcevz7jc">
            Ver oferta
          </button>
        </div>
      </header>

      {/* HERO */}
      <section ref={heroSectionRef} className="relative overflow-hidden bg-[#0B1F3B] px-4 pb-14 pt-5 text-white sm:pb-16 md:pb-20 md:pt-20" style={{
      paddingBottom: "16px"
    }} data-id="element-190" data-mp="3p8g2hmsdbixh">
        <div className="relative z-10 mx-auto max-w-5xl text-center" data-id="element-191" data-mp="14mo75wi5mqp7">
          <p className="mx-auto mb-3 inline-flex h-7 items-center gap-1.5 rounded-full bg-[#EF3035] px-3 text-[9px] font-black tracking-[0.06em] text-white shadow-sm sm:text-xs" data-id="element-192" data-mp="zkp4kou8nxtb">
            <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" data-id="element-193" data-mp="2miggurd21um3" />
            ATENCIÓN: ELECTRICISTAS DOMICILIARIOS
          </p>

          <h1 className="mx-auto max-w-4xl pt-4 text-[31px] font-black leading-[0.94] tracking-[-0.055em] sm:text-5xl md:text-7xl" data-id="element-194" data-mp="28k9qrxeckywz">
            <span className="block whitespace-nowrap" data-id="element-195" data-mp="3172zp31w54i2"><span className="text-[#F5C844]" data-id="element-196" data-mp="318dzysw4nu4f">+100</span> INSTALACIONES</span>
            <span className="mt-1 block whitespace-nowrap" data-id="element-197" data-mp="3597qzv4v9p1j">ELÉCTRICAS <span className="text-[#F5C844]" data-id="element-198" data-mp="35asgh6cbqhsj">RESUELTAS</span></span>
          </h1>

          <p className="mx-auto mt-5 max-w-xl text-center text-sm font-black leading-snug text-white sm:text-base md:text-xl" data-id="element-199" data-mp="2rh17q06f31xv">
            ENCONTRÁ EL ESQUEMA QUE NECESITÁS,<br data-id="element-200" data-mp="3k5oxa7y78dh8" />
            <span className="text-[#F5C844]" data-id="element-201" data-mp="3k56u0rd4cyzt">SIN PERDER TIEMPO</span> BUSCANDO.
          </p>

          <div ref={heroFanRef} className={`hero-product-stage hero-fan-stage relative mx-auto mt-5 flex w-full max-w-[560px] justify-center sm:mt-7 ${heroFanVisible ? 'hero-fan-visible' : ''} ${heroFanExpanded ? 'hero-fan-expanded' : ''} ${heroFanPulseAnimating ? 'hero-fan-pulse-active' : ''}`} onMouseEnter={() => {
          heroInactivityResetRef.current();
          setHeroFanExpanded(true);
        }} onMouseLeave={() => {
          heroInactivityResetRef.current();
          setHeroFanExpanded(false);
        }} onTouchStart={() => {
          heroInactivityResetRef.current();
          suppressHeroBookClickRef.current = true;
          setHeroFanExpanded(true);
          if (heroTouchTimerRef.current) window.clearTimeout(heroTouchTimerRef.current);
          heroTouchTimerRef.current = window.setTimeout(() => setHeroFanExpanded(false), 650);
        }} data-id="element-202" data-mp="3oxqgkdtsb4gr">
            <div className="hero-product-shadow pointer-events-none absolute inset-x-[18%] bottom-[8%] z-0 h-5" aria-hidden="true" data-id="element-203" data-mp="mggmpyhgaiv4"></div>
            <div className="hero-fan-sheets pointer-events-none absolute inset-0 z-[1]" aria-hidden="true" data-id="element-204" data-mp="idk80iv176wg">
              {heroSheets.map((sheet, index) => {
              const sheetProgress = Math.max(0, Math.min(1, (heroFanProgress - index * 0.07) / 0.51));
              const spread = heroFanExpanded ? 0.99 : heroFanPulse ? 0.95 : 0.9;
              const translateX = sheet.x * sheetProgress * spread;
              const translateY = sheet.y * sheetProgress;
              const rotation = sheet.rotate * sheetProgress;
              const scale = 0.74 + (sheet.scale - 0.74) * sheetProgress;
              return <img key={sheet.src} src={sheet.src} alt="" className="hero-fan-sheet" style={{
                transform: `translate3d(calc(-50% + ${translateX}px), calc(-50% + ${translateY}px), 0) rotate(${rotation}deg) scale(${scale})`,
                opacity: sheetProgress,
                zIndex: sheet.depth
              }} data-id="element-205" data-mp="3hpqbtchc6cni" data-mpi={index} />;
            })}
            </div>
            <div className="hero-product-float relative z-10 w-[84%] md:w-[82%]" data-id="element-206" data-mp="1wd5d86iw0bdx">
              <img src="https://cdn.magicpatterns.com/uploads/r7JhcpdYiLyVgbmAQHch52/Diseno_sin_titulo_(5).webp" alt="+100 Instalaciones Eléctricas Resueltas" className="hero-product-enter block h-auto w-full cursor-pointer object-contain" onClick={() => {
              if (suppressHeroBookClickRef.current) {
                suppressHeroBookClickRef.current = false;
                return;
              }
              scrollToPurchase();
            }} data-id="element-207" data-mp="1hz8el5kuwh3j" />
            </div>
          </div>

          <div className="mx-auto mt-5 flex w-full max-w-md items-center justify-between px-2 text-white sm:mt-7 sm:px-0" data-id="element-208" data-mp="25p2opfdsjrk3">
            <div className="flex min-w-0 flex-1 flex-col items-center gap-1 text-center" data-id="element-209" data-mp="1roi6akbsfxr4">
              <BookOpen className="h-4 w-4 text-[#F5C844]" data-id="element-210" data-mp="1vqdjam32zzkq" />
              <span className="text-[9px] font-black leading-tight sm:text-[11px]" data-id="element-211" data-mp="1vszqulpz2f2y">100 INSTALACIONES</span>
            </div>
            <div className="flex min-w-0 flex-1 flex-col items-center gap-1 text-center" data-id="element-212" data-mp="kbq49qbjr6xf">
              <Smartphone className="h-4 w-4 text-[#F5C844]" data-id="element-213" data-mp="cs762ctqf749" />
              <span className="text-[9px] font-black leading-tight sm:text-[11px]" data-id="element-214" data-mp="cqmcoqj5lu7z">APP DE CONSULTA</span>
            </div>
            <div className="flex min-w-0 flex-1 flex-col items-center gap-1 text-center" data-id="element-215" data-mp="7vqlfslqravx">
              <Gift className="h-4 w-4 text-[#F5C844]" data-id="element-216" data-mp="moaiqcm7mo5g" />
              <span className="text-[9px] font-black leading-tight sm:text-[11px]" data-id="element-217" data-mp="hslcjqfoq906">5 BONOS INCLUIDOS</span>
            </div>
          </div>

          <p className="mx-auto mt-6 max-w-md text-center text-[13px] font-semibold leading-snug text-white sm:mt-7 sm:text-sm" data-id="element-218" data-mp="3q5936ntkcekg">
            Dejá de buscar entre <span className="font-black text-[#F5C844]" data-id="element-219" data-mp="3su6cbvetwwq">videos, grupos y apuntes</span>. Tené tus instalaciones organizadas en una <span className="font-black text-[#F5C844]" data-id="element-220" data-mp="3v4vjx78locy0">APP con buscador y asistente</span>, listas para consultar desde tu celular.
          </p>

          <div className="mx-auto mt-4 aspect-[4.4/1] w-[98%] max-w-[620px] overflow-hidden" data-id="element-221" data-mp="32hsnqlreceax">
            <img src="https://cdn.magicpatterns.com/uploads/6n8WwqJ5DhcLAdBGsYTXBd/Instalaciones_electricas_con_confianza.webp" alt="Profesionales usan +100 Instalaciones Eléctricas Resueltas" className="block h-full w-full object-cover object-[center_35%]" data-id="element-222" data-mp="3s5wqxpsfswv9" />
          </div>

          <button onClick={scrollToPurchase} className="mt-4 inline-flex w-full max-w-md items-center justify-center gap-2 rounded-xl bg-[#F5C844] px-6 py-3.5 text-base font-black text-[#0B1F3B] shadow-lg shadow-black/25 transition-[background-color,transform] duration-200 hover:bg-[#ffe078] sm:mt-5 sm:w-auto sm:px-8 sm:py-4 sm:text-xl" data-id="element-223" data-mp="4uxe5fotb0y2">
            ⚡ ¡QUIERO MIS 100 INSTALACIONES!
          </button>

          <div className="mx-auto mt-4 flex max-w-md items-center justify-center gap-1.5 whitespace-nowrap text-[8px] font-bold tracking-wide text-blue-100/85 sm:gap-3 sm:text-[11px]" data-id="element-224" data-mp="3i9tpyn9bn49w">
            <span className="inline-flex items-center gap-1" data-id="element-225" data-mp="xh9rgkxfi1ou"><Zap className="h-3 w-3 text-[#F5C844]" aria-hidden="true" data-id="element-226" data-mp="12g35cegmnupj" /> ACCESO INMEDIATO</span>
            <span className="text-blue-300/40" data-id="element-227" data-mp="2mz6mojn0n1xr">|</span>
            <span className="inline-flex items-center gap-1" data-id="element-228" data-mp="2mxvgyea0fk40"><Lock className="h-3 w-3 text-[#F5C844]" aria-hidden="true" data-id="element-229" data-mp="2hz0savpd31bz" /> PAGO SEGURO</span>
            <span className="text-blue-300/40" data-id="element-230" data-mp="2d8kakzeictjt">|</span>
            <span className="inline-flex items-center gap-1" data-id="element-231" data-mp="2d70y7jvz32nb"><Download className="h-3 w-3 text-[#F5C844]" aria-hidden="true" data-id="element-232" data-mp="28aaiwyrzsb2y" /> MATERIAL DIGITAL PDF</span>
          </div>
        </div>
      </section>

      <ProblemSolutionSection data-id="element-233" data-mp="249gve6igoyal" />
      <MaterialPreviewSection data-id="element-234" data-mp="1z92mkm91dkbi" />
      <MaestroElectricModules data-id="element-235" data-mp="1z81807stafp1" />

      <MaestroElectricBonuses data-id="element-236" data-mp="1z7rh8hzy04ia" />

      {/* REFERENCE COMPARISON */}
      <section id="benefits-section" className="bg-[#0B1F3B] px-5 py-11 text-white sm:px-8 md:py-16" aria-labelledby="reference-comparison-title" style={{
      paddingTop: "8px",
      paddingBottom: "15px"
    }} data-id="element-237" data-mp="1ub0zlq8hp0ur">
        <div className="mx-auto max-w-5xl" data-id="element-238" data-mp="3onh4onpju8y1">
          <div className="max-w-3xl" data-id="element-239" data-mp="3jowbv9cmmrjm">
            <span className="block h-px w-10 bg-[#F5C844]" aria-hidden="true" data-id="element-240" data-mp="3jpz1ldy7qnxy" />
            <h2 id="reference-comparison-title" className="mt-4 text-[32px] font-black leading-[0.96] tracking-[-0.045em] sm:text-5xl" data-id="element-241" data-mp="3rp2brvjtoiyb">
              <span className="block text-white" data-id="element-242" data-mp="isnftztoi0y">UNA DUDA CHICA.</span>
              <span className="mt-1 block text-[#EF3035]" data-id="element-243" data-mp="3hs7jvvi21gt0">TODO EL TRABAJO FRENADO.</span>
            </h2>
          </div>

          <div className="relative mt-7 grid gap-4 border-t border-white/15 pt-6 md:mt-9 md:grid-cols-2 md:gap-5 md:pt-7" style={{
          gap: "7px"
        }} data-id="element-244" data-mp="3moxvo6mttq8m">
            <div className="absolute inset-x-0 top-[-1px] h-px bg-[linear-gradient(90deg,#EF3035_0%,#EF3035_42%,#10B981_58%,#10B981_100%)]" aria-hidden="true" data-id="element-245" data-mp="3dqcjct7y77ga" />
            <article className="rounded-2xl border border-[#EF3035]/35 bg-[#EF3035]/[0.06] p-5 shadow-[0_10px_24px_rgba(3,13,31,0.10)] sm:p-6" style={{
            paddingTop: "5px",
            paddingBottom: "5px"
          }} data-id="element-246" data-mp="2yytwcbgo8cnf">
              <div className="flex items-center gap-3 border-b border-[#EF3035]/20 pb-4" style={{
              paddingBottom: "0px"
            }} data-id="element-247" data-mp="2u2d538e6di64">
                <span className="flex h-8 w-8 items-center justify-center border border-[#EF3035]/60 bg-[#EF3035]/15 text-[#ff6d70]" data-id="element-248" data-mp="2bdt8yfcxtfsa"><X className="h-5 w-5" aria-hidden="true" data-id="element-249" data-mp="26gj7g1c9xdo3" /></span>
                <h3 className="text-base font-black tracking-wide text-white sm:text-lg" data-id="element-250" data-mp="2kf0t7timcg3d">SIN UNA REFERENCIA A MANO</h3>
              </div>
              <ul className="mt-4 space-y-4 text-[15px] font-medium leading-snug text-white sm:text-base" data-id="element-251" data-mp="2fhhfqvtqw7ch">
                {['La instalación espera mientras buscás una respuesta.', 'Saltás entre videos y PDFs sin encontrar tu caso.', 'En otro trabajo, la misma duda te vuelve a frenar.'].map(item => <li key={item} className="grid grid-cols-[28px_1fr] items-start gap-3" data-id="element-252" data-mp="336tm7nxou1b3"><X className="mt-0.5 h-6 w-6 text-[#ff6d70]" aria-hidden="true" data-id="element-253" data-mp="2z4f4j75r97le" /><span data-id="element-254" data-mp="3d2n5rr9tqv2d">{item}</span></li>)}
              </ul>
            </article>

            <article className="rounded-2xl border border-emerald-400/35 bg-emerald-400/[0.06] p-5 shadow-[0_10px_24px_rgba(3,13,31,0.10)] sm:p-6" style={{
            paddingTop: "7px",
            paddingBottom: "7px"
          }} data-id="element-255" data-mp="383tu84e3le4u">
              <div className="flex items-center gap-3 border-b border-emerald-400/20 pb-4" style={{
              paddingBottom: "7px"
            }} data-id="element-256" data-mp="29r7nq1jq2wkg">
                <span className="flex h-8 w-8 items-center justify-center border border-emerald-400/60 bg-emerald-400/15 text-emerald-300" data-id="element-257" data-mp="2ep98qs0pl5yk"><Check className="h-5 w-5" aria-hidden="true" data-id="element-258" data-mp="2oh5ix1g2kmj5" /></span>
                <h3 className="text-base font-black tracking-wide text-white sm:text-lg" data-id="element-259" data-mp="2oiztpmxmmy9i">CON +100 INSTALACIONES ELÉCTRICAS RESUELTAS</h3>
              </div>
              <ul className="mt-4 space-y-4 text-[15px] font-medium leading-snug text-white sm:text-base" data-id="element-260" data-mp="2ximx2e9x2e57">
                {['Ubicás la lámina relacionada y encontrás qué consultar.', 'Ves componentes, conexiones y puntos para revisar.', 'Tenés la referencia a mano para volver a consultarla.'].map(item => <li key={item} className="grid grid-cols-[28px_1fr] items-start gap-3" data-id="element-261" data-mp="181u9rb50ewxe"><Check className="mt-0.5 h-6 w-6 text-emerald-300" aria-hidden="true" data-id="element-262" data-mp="10j4h4vaf3gks" /><span data-id="element-263" data-mp="10kfnn2v8z2fl">{item}</span></li>)}
              </ul>
            </article>
          </div>
        </div>
      </section>

      {/* STUDENT SOCIAL PROOF */}
      <section className="bg-white px-4 pt-6" style={{
      paddingTop: "0px",
      paddingLeft: "1px",
      paddingRight: "0px"
    }} data-id="element-264" data-mp="quctv0gaxkg7">
        <figure className="mx-auto max-w-5xl overflow-hidden rounded-2xl" data-id="element-265" data-mp="ls5c1u8sja5y">
          <img src="https://cdn.magicpatterns.com/uploads/uJcmg56Kn8JwS78GSR6sfi/ChatGPT_Image_8_ago_2026,_23_17_17.jpg" alt="Comunidad de alumnos de +100 Instalaciones Eléctricas Resueltas" className="block h-auto w-full object-contain" data-id="element-266" data-mp="1jgnq5ywvkz77" />
        </figure>
      </section>

      {/* PURCHASE SECTION */}
      <section id="compra" className="w-full bg-sky-50 px-3 py-8 md:px-4 md:py-12" style={{
      paddingTop: "0px",
      paddingBottom: "13px"
    }} data-id="element-267" data-mp="15ho3uxwu4we7">
        <div className="mx-auto w-full max-w-5xl" data-id="element-268" data-mp="3q8g6vb3h86j4">
          <div className="mx-auto w-full max-w-[390px] overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_14px_32px_rgba(15,37,73,0.12)] md:max-w-none" data-id="element-269" data-mp="3q7nan4258zf8">
            <div className="mx-auto grid w-full max-w-[390px] items-center gap-4 p-4 md:max-w-none md:grid-cols-[1.05fr_0.95fr] md:gap-8 md:p-7" data-id="element-270" data-mp="1y75fq2mngs8q">
              <div className="order-1 flex w-full justify-center md:order-2" data-id="element-271" data-mp="1k8ysdgfbuanb">
                <img src="https://cdn.magicpatterns.com/uploads/xm2QTGbpvKb1gPGg4nNAkj/Create_commercial_electrician_ki…_20261024185706.webp" alt="+100 Instalaciones Eléctricas Resueltas con cinco bonos" className="h-auto w-full max-w-[300px] object-contain md:max-w-[380px]" data-id="element-272" data-mp="1p70anz4xi5xq" />
              </div>
              <div className="order-2 text-center md:order-1 md:text-left" data-id="element-273" data-mp="2356ysnk2srk7">
                <p className="text-[10px] font-black tracking-[0.14em] text-[#0B1F3B]/60" data-id="element-274" data-mp="2849ql6b1p1a8">KIT DIGITAL PARA ELECTRICISTAS</p>
                <h2 className="mt-1 text-2xl font-black leading-tight text-[#0B1C3B] md:text-4xl" data-id="element-275" data-mp="2vtvsw2mprwmg">+100 INSTALACIONES ELÉCTRICAS RESUELTAS</h2>
                <p className="mx-auto mt-2 max-w-xl text-[13px] leading-snug text-slate-600 md:mx-0 md:text-sm" data-id="element-276" data-mp="3a063yormdpj1">Material de consulta para resolver instalaciones domiciliarias desde tu celular.</p>
                <p className="mt-4 border-y border-slate-200 py-3 text-sm font-black tracking-tight text-[#0B1C3B]" style={{
                paddingTop: "5px",
                paddingBottom: "6px"
              }} data-id="element-277" data-mp="35wrivjotqiyq">100 instalaciones <span className="text-[#F5C844]" data-id="element-278" data-mp="2r1bo1cgf6j1a">+</span> 10 módulos <span className="text-[#F5C844]" data-id="element-279" data-mp="2r238ps8hawcs">+</span> APP <span className="text-[#F5C844]" data-id="element-280" data-mp="2mzq3r61k32h2">+</span> 5 bonos</p>
                <div className="mt-4 border-t border-slate-200 pt-4 text-center" style={{
                paddingTop: "0px"
              }} data-id="element-281" data-mp="2n1jccqvqbwrp">
                  <span className="inline-flex rounded-full bg-[#EF3035] px-2.5 py-1 text-[10px] font-black tracking-[0.12em] text-white" data-id="element-282" data-mp="2w0ekw9m8xiy6">50% OFF</span>
                  <p className="mt-2 text-[11px] font-bold tracking-wide text-slate-500" data-id="element-283" data-mp="g7pa61yu0pty">INVERSIÓN ÚNICA</p>
                  <p className="mt-0.5 text-sm font-bold text-slate-400 line-through" data-id="element-284" data-mp="b9xjtcdu5brv">ARS 39.980</p>
                  <p className="mt-0.5 text-5xl font-black leading-none tracking-tight text-emerald-600 md:text-6xl" data-id="element-285" data-mp="23i1qafs2ukcr">ARS 19.990</p>
                  <button type="button" onClick={handlePurchaseClick} className="purchase-cta-shimmer relative mt-3 flex min-h-[60px] w-full items-center justify-center gap-2 overflow-hidden rounded-xl bg-emerald-600 px-5 text-center text-base font-black text-white shadow-[0_6px_0_#167a45,0_14px_22px_rgba(22,163,74,0.24)] transition-[transform,box-shadow,background-color] duration-180 ease-out hover:bg-emerald-500 active:translate-y-[2px] active:shadow-[0_3px_0_#167a45,0_8px_14px_rgba(22,163,74,0.2)] md:text-lg" data-id="element-286" data-mp="2d9pmetzkwpkk"><span className="relative z-10" data-id="element-287" data-mp="1pk4qlaibtxtv">⚡ ¡QUIERO MIS 100 INSTALACIONES!</span></button>
                  <img src="https://cdn.magicpatterns.com/uploads/hgUps8NNywDJCXoWVVgxqr/capture_260410_163646.png" alt="Pago seguro con Visa, Mastercard, American Express y Mercado Pago" className="mx-auto mt-3 h-auto w-full max-w-[330px] object-contain" data-id="element-288" data-mp="350vgotsmbt1x" />
                  <p className="mt-2 flex items-center justify-center gap-2 text-center text-[10px] font-bold tracking-[0.08em] text-slate-500" data-id="element-289" data-mp="38h820gstf3fn"><Download className="h-3.5 w-3.5 text-[#0B1C3B]" aria-hidden="true" data-id="element-290" data-mp="3dft3ejjszt5o" /> ACCESO DIGITAL <span aria-hidden="true" data-id="element-291" data-mp="2ymg4te35aile">·</span> <Lock className="h-3.5 w-3.5 text-[#0B1C3B]" aria-hidden="true" data-id="element-292" data-mp="33jq4dmk90amy" /> PAGO SEGURO</p>
                  <img src="https://cdn.magicpatterns.com/uploads/rb28ac8338XVN1wavuA4BV/capture_261005_175233.png" alt="Referencias técnicas AEA 90364 e IRAM" className="mx-auto mt-3 h-auto w-full max-w-[220px] object-contain" data-id="element-293" data-mp="3r9apoc3vjjze" />
                </div>
              </div>
            </div>
          </div>
        </div>
      <section className="bg-white px-4 pb-5 pt-0" style={{
        paddingBottom: "0px",
        paddingLeft: "0px",
        paddingRight: "0px"
      }} data-id="element-294" data-mp="3mi2bz3vihcgf">
        <div className="mx-auto max-w-4xl" data-reveal data-id="element-295" data-mp="e17lknvwhkj8">
          <article className="technical-reveal overflow-hidden rounded-2xl border border-blue-100 bg-[#F4F7FA] p-4 sm:p-5" style={{
            paddingBottom: "0px"
          }} data-id="element-296" data-mp="iu0pj9jjoou9">
            <div className="flex items-center gap-4 sm:gap-6" data-id="element-297" data-mp="sn3i7zubdscr">
              <div className="flex h-24 w-24 flex-shrink-0 items-center justify-center overflow-hidden rounded-xl border border-[#0B1F3B]/10 bg-[#0B1F3B] sm:h-28 sm:w-28" data-id="element-298" data-mp="nqwb43q6mat8">
                <img src="https://cdn.magicpatterns.com/uploads/ieYDWfp3ro3Pv7oi4Gc8sn/Diseno_sin_titulo.png" alt="Sello Acceso de por vida" className="h-full w-full scale-125 object-contain mix-blend-screen" data-id="element-299" data-mp="eqhmaue0edkz" />
              </div>
              <div className="min-w-0" data-id="element-300" data-mp="4ybzdz2m6mcn">
                <h3 className="text-xl font-black leading-[1.02] tracking-[-0.03em] text-[#0B1F3B] sm:text-2xl" data-id="element-301" data-mp="s9u30gun5e">TU MATERIAL, PARA SIEMPRE</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600 sm:text-base" data-id="element-302" data-mp="3s5w43qazg9hx">Pagás una sola vez y podés volver a consultar tus 100 instalaciones, módulos y bonos cuando lo necesites.</p>
                <span className="mt-3 inline-flex items-center rounded-full bg-emerald-100 px-3 py-1.5 text-xs font-black text-emerald-700" data-id="element-303" data-mp="3i7gexsvinab2">✓ Acceso de por vida incluido</span>
              </div>
            </div>
          </article>
        </div>
      </section></section>

      {/* LIFETIME ACCESS */}
      

      {/* CONTENT PREVIEW */}
      

      {/* TESTIMONIALS */}
      <section className="py-16 px-4 bg-gradient-to-b from-gray-100 to-gray-50" style={{
      paddingTop: "19px",
      paddingBottom: "0px"
    }} data-id="element-304" data-mp="2bono2pmizfnh">
        <div className="container mx-auto max-w-2xl" data-reveal data-id="element-305" data-mp="1ez6brrt1evct">
          <div className="text-center mb-8" data-id="element-306" data-mp="1jwg7fp70s393">
            <div className="mb-3 flex items-center justify-center gap-1" data-id="element-307" data-mp="1jx7s44z2wgkl">
              {[1, 2, 3, 4, 5].map(star => <Star key={star} className="h-5 w-5 fill-orange-400 text-orange-400" aria-hidden="true" data-id="element-308" data-mp="1nx9rg7hccxwp" />)}
            </div>
            <h2 className="text-xl font-bold uppercase tracking-tight text-gray-900 md:text-2xl" data-id="element-309" data-mp="w7dg1pml2w1e">
              MÁS DE 3.200 ALUMNOS RESPALDAN NUESTRA COMUNIDAD
            </h2>
            <p className="mx-auto mt-2 max-w-md text-sm text-gray-500" data-id="element-310" data-mp="106vzegyraz24">
              Alumnos de la comunidad Maestro Eléctrico.
            </p>
          </div>

          <div className="space-y-4" data-id="element-311" data-mp="c6bvyd4ws7ut">
            {allChatReviews.map((t, i) => <TestimonialItem key={i} t={t} data-id="element-312" data-mp="qtwxw3q1s115" data-mpi={i} />)}
          </div>
        </div>
      </section>

      <section ref={finalComboRef} className="bg-white px-4 py-9 md:py-12" aria-labelledby="final-offer-title" data-id="element-313" data-mp="m3glmmxyqlgd">
        <div className={`mx-auto max-w-xl overflow-hidden rounded-2xl border border-[#D6A44B]/80 bg-[#0B1C3B] px-5 py-6 text-center shadow-[0_10px_24px_rgba(11,28,59,0.18)] md:px-8 ${finalComboVisible ? 'final-combo-enter' : 'opacity-0'}`} data-id="element-314" data-mp="zulf17aqmhi9">
          <span className="inline-flex items-center gap-1 rounded-full border border-[#D6A44B]/70 bg-[#D6A44B]/10 px-3 py-1 text-[10px] font-black tracking-[0.12em] text-[#E8C272]" data-id="element-315" data-mp="19sizdjph7u5l"><Zap className="h-3 w-3" aria-hidden="true" data-id="element-316" data-mp="3bjmbkdf0lgs9" /> ANTES DE IRTE...</span>
          <h2 id="final-offer-title" className="mx-auto mt-4 max-w-md text-2xl font-black leading-tight text-white md:text-3xl" data-id="element-317" data-mp="36lktnvtbrnid">TU KIT COMPLETO: <span className="text-orange-400" data-id="element-318" data-mp="1m4bp2ho21235">+100 INSTALACIONES ELÉCTRICAS RESUELTAS</span></h2>
          <p className="mx-auto mt-3 max-w-md text-[13px] leading-relaxed text-blue-100/80 md:text-sm" data-id="element-319" data-mp="1r1bt6n1cfurm">100 instalaciones resueltas, 10 módulos, una APP de consulta y 5 bonos prácticos. Todo organizado para tenerlo a mano cuando lo necesites.</p>

          <img src="https://cdn.magicpatterns.com/uploads/xm2QTGbpvKb1gPGg4nNAkj/Create_commercial_electrician_ki…_20261024185706.webp" alt="+100 Instalaciones Eléctricas Resueltas con cinco bonos" className="mx-auto mt-4 block h-auto w-full max-w-[330px] object-contain" data-id="element-320" data-mp="1ha7cniw0s1uo" />

          <div className="mt-3 border-t border-[#D6A44B]/25 pt-3 text-left" data-id="element-321" data-mp="tg635rl9l71q">
            <p className="text-center text-xs font-black tracking-wide text-[#E8C272]" data-id="element-322" data-mp="yer09i57zaly">+100 INSTALACIONES ELÉCTRICAS RESUELTAS</p>
            <ul className="mx-auto mt-3 max-w-sm space-y-1.5 text-xs leading-snug text-blue-100/85" data-id="element-323" data-mp="2ntfgjugxlcp9">
              {['100 instalaciones eléctricas resueltas', '10 módulos organizados por temática', 'APP de consulta con buscador y asistente', '5 bonos para diagnosticar, verificar y presupuestar', 'Acceso inmediato y de por vida'].map(item => <li key={item} className="flex items-start gap-2" data-id="element-324" data-mp="2hcdrnzlssuj4"><Check className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-[#E8C272]" aria-hidden="true" data-id="element-325" data-mp="2hd5iyy8d463l" />{item}</li>)}
            </ul>
          </div>

          <div className="mt-4 rounded-lg border border-blue-200/60 bg-blue-50/10 px-3 py-2.5 text-left" data-id="element-326" data-mp="3a11j6cx54g54">
            <p className="flex items-center gap-1.5 text-xs font-black tracking-wide text-blue-100" data-id="element-327" data-mp="352880r5bt184"><Download className="h-4 w-4 flex-shrink-0 text-[#E8C272]" aria-hidden="true" data-id="element-328" data-mp="30b8b3lw7ij5m" /> FORMATO 100% DIGITAL</p>
            <p className="mt-1 text-[11px] leading-relaxed text-blue-100/80" data-id="element-329" data-mp="2vconlh1yszb5">Guías y láminas digitales para consultar desde tu celular, tablet o computadora. Acceso inmediato, sin envío físico.</p>
          </div>
          <p className="mt-4 text-4xl font-black tracking-tight text-[#39D98A]" data-id="element-330" data-mp="3o0kfm8gla31l">AR$ 19.990</p>
          <div className="mt-4 rounded-xl border border-[#D6A44B]/80 bg-[#10254b] px-4 py-3 text-left shadow-[0_0_14px_rgba(214,164,75,0.12)]" data-id="element-331" data-mp="4knrqsu86il">
            <p className="flex items-center gap-2 text-sm font-black text-[#E8C272]" data-id="element-332" data-mp="45xq3qwq1rkr"><Gift className="h-4 w-4 flex-shrink-0" aria-hidden="true" data-id="element-333" data-mp="93pl4tsnxtt6" /> 5 BONOS PRÁCTICOS INCLUIDOS</p>
            <p className="mt-1.5 text-xs leading-relaxed text-blue-100/85" data-id="element-334" data-mp="e4mx2on2xrm8">Sumá herramientas para diagnosticar, verificar, presupuestar y relevar instalaciones eléctricas en cada trabajo.</p>
          </div>

          <div className="final-offer-urgency relative mt-4 overflow-hidden rounded-xl border border-[#D6A44B]/75 bg-[#10254b] px-4 py-3.5 text-left" data-id="element-335" data-mp="n2yxfidr03rv">
            <p className="relative z-10 flex items-center gap-2 text-sm font-black text-[#F5C844]" data-id="element-336" data-mp="wu1xqhyuseq3"><Gift className="h-4 w-4 flex-shrink-0" aria-hidden="true" data-id="element-337" data-mp="11teld4xjtptq" /> ¡APROVECHÁ EL 50% OFF!</p>
            <p className="relative z-10 mt-1.5 text-xs leading-relaxed text-blue-100" data-id="element-338" data-mp="16siszr4trfhy">Llevate las +100 Instalaciones Eléctricas Resueltas, los 10 módulos, la APP y los 5 bonos por ARS 19.990.</p>
            <p className="relative z-10 mt-2 text-[11px] font-black tracking-wide text-[#F5C844]" data-id="element-339" data-mp="1s5ai31i3so55">⚡ Precio promocional por tiempo limitado.</p>
          </div>

          <button type="button" onClick={handlePurchaseClick} className="mt-5 flex min-h-[62px] w-full items-center justify-center gap-2 rounded-2xl bg-emerald-600 px-4 text-center text-sm font-black text-white shadow-[0_6px_0_#167a45,0_14px_24px_rgba(22,163,74,0.24)] transition-[transform,box-shadow,background-color] duration-180 ease-out hover:bg-emerald-500 active:translate-y-[2px] active:shadow-[0_3px_0_#167a45,0_8px_14px_rgba(22,163,74,0.2)] md:text-base" data-id="element-340" data-mp="212ukwevm6shv">⚡ ¡QUIERO MIS 100 INSTALACIONES!</button>

          <div className="mt-4 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-[10px] font-bold tracking-wide text-blue-100/80" data-id="element-341" data-mp="1dd0eog5sw26l">
            <span className="inline-flex items-center gap-1" data-id="element-342" data-mp="kq5s9dpfasqh"><Lock className="h-3.5 w-3.5 text-[#E8C272]" aria-hidden="true" data-id="element-343" data-mp="kpcv94g9nhlj" /> PAGO SEGURO</span>
            <span className="inline-flex items-center gap-1" data-id="element-344" data-mp="2wbohx4b7ycmj"><Zap className="h-3.5 w-3.5 text-[#E8C272]" aria-hidden="true" data-id="element-345" data-mp="313ppicxfzf68" /> ACCESO INMEDIATO</span>
            <span className="inline-flex items-center gap-1" data-id="element-346" data-mp="2nctajowfuwhn"><Download className="h-3.5 w-3.5 text-[#E8C272]" aria-hidden="true" data-id="element-347" data-mp="2ne4f3qxpk89t" /> ACCESO DE POR VIDA</span>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 px-4 bg-white" style={{
      paddingTop: "0px",
      paddingBottom: "0px"
    }} data-id="element-348" data-mp="3g0yw2dvbgpij">
        <img src="https://cdn.magicpatterns.com/uploads/uiAkrJRfHpcu4UFuy6ko6E/capture_261005_175233.png" alt="Referencias técnicas argentinas AEA 90364 e IRAM" className="mx-auto block w-full max-w-[330px] object-contain" data-id="element-349" data-mp="3k33ppclrlm56" /><div className="container mx-auto max-w-3xl" data-id="element-350" data-mp="3b0ueslml3qr9">
          <div className="text-center mb-12" data-id="element-351" data-mp="1unvu7rbsf9rj">
            <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-4" data-id="element-352" data-mp="1un49xcnn4ygk">
              Resolvé acá tus dudas
            </h2>
            <p className="text-lg text-gray-600" data-id="element-353" data-mp="1zkw2m8w40olt">
              Hacé clic para ver la respuesta que necesitás
            </p>
          </div>

          <div className="space-y-3" data-id="element-354" data-mp="2d24hgzgd842f">
            <FAQItem question="¿Qué incluye +100 Instalaciones Eléctricas Resueltas?" answer="Incluye 100 instalaciones eléctricas resueltas, organizadas en 10 módulos temáticos, una APP de consulta con buscador y asistente, y 5 bonos prácticos para diagnosticar, verificar, organizar materiales, relevar y presupuestar." data-id="element-355" data-mp="2d12t5tac9l2r" />
            <FAQItem question="¿Qué tipo de instalaciones voy a encontrar?" answer="Vas a encontrar esquemas de iluminación, tomacorrientes, sensores, tableros y protecciones, equipos de uso dedicado, bombas y flotantes, motores, timbres y circuitos auxiliares, además de instalaciones completas de ambientes." data-id="element-356" data-mp="1pbi9f86d2jsf" />
            <FAQItem question="¿Puedo consultarlo desde el celular?" answer="Sí. El material está pensado para consultarlo desde celular, tablet o computadora. La APP reúne las láminas para que encuentres rápidamente la instalación relacionada." data-id="element-357" data-mp="16n3islzyh7s5" />
            <FAQItem question="¿Cómo funciona la APP de consulta?" answer="La APP te ayuda a ubicar el tema que necesitás mediante su buscador y asistente, para revisar componentes, conexiones y puntos clave sin tener que recorrer videos o PDFs sueltos." data-id="element-358" data-mp="3ouwo5ge9p7v7" />
            <FAQItem question="¿Qué son los 5 bonos incluidos?" answer="Son herramientas prácticas: Diagnóstico rápido de fallas eléctricas, Checklist de verificación eléctrica, Guía de materiales por instalación, Presupuestador de instalaciones eléctricas y Guía de relevamiento eléctrico." data-id="element-359" data-mp="3awow9mw38wqw" />
            <FAQItem question="¿En qué formato recibo el material?" answer="Todo se entrega en formato 100% digital, con acceso inmediato a las guías y láminas. No hay envío físico." data-id="element-360" data-mp="2x4s9lyd6vf1w" />
            <FAQItem question="¿Tengo acceso de por vida?" answer="Sí. Una vez que accedés, el material queda disponible para que puedas volver a consultarlo cuando lo necesites." data-id="element-361" data-mp="2s44bytugfzyp" />
          </div>
        </div>
      </section>

      <UpsellSection data-id="element-362" data-mp="28jm5hwr2soqz" />

      <section className="border-t border-slate-200 bg-slate-50 px-4 py-8" aria-label="Referencias técnicas" style={{
      paddingBottom: "0px"
    }} data-id="element-363" data-mp="28jdt05yv7lgb">
        <div className="mx-auto max-w-sm text-center" data-id="element-364" data-mp="2dgvu5a6n4wdr">
          
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-gray-900 text-gray-400 py-10 px-4 text-center text-sm border-t border-gray-800" style={{
      paddingTop: "14px",
      paddingBottom: "21px"
    }} data-id="element-365" data-mp="23ksr823ystpr">
        <div className="container mx-auto" data-id="element-366" data-mp="2rbewxmqhy1wl">
          <div className="w-12 h-12 bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-367" data-mp="2wa9eymgn3mfn">
            <img src="https://cdn.magicpatterns.com/uploads/8PWeJYoMntMjFBhKbpgq4k/Emblema_de_puno_electrico_2.0.png" alt="Logo Maestro Eléctrico" className="w-8 h-8 object-contain opacity-80" data-id="element-368" data-mp="2ij4mq5flpwfc" />
          </div>
          <p className="mb-2" data-id="element-369" data-mp="3a8g4nfbfqg48">
            &copy; 2026 Maestro Eléctrico. Todos los derechos reservados.
          </p>
          <p className="text-gray-500" data-id="element-370" data-mp="3f1shks6r4yh5">
            Material digital de consulta para instalaciones eléctricas domiciliarias.
          </p>
        </div>
      </footer>
      {notification && <PurchaseNotification notification={notification} isVisible={isVisible} data-id="element-371" data-mp="yle1u6za51am" />}
    </div>;
}